import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["eventFilterId"];
export const getRefinementListUpdates = (oldRefinementList, maybeRefinementList) => {
  if (oldRefinementList) {
    // If we deleted refinements, send a DELETE update object
    if (!maybeRefinementList) {
      return [{
        listId: oldRefinementList.listId,
        updateType: 'DELETE',
        listTypes: ['REFINE_BY_LIST']
      }];
    }

    // If the new refinement list has no list id, this indicates that the list was deleted and
    // a new one was recreated. In this case DELETE the old one and CREATE a new one
    if (!maybeRefinementList.listId) {
      return [{
        listId: oldRefinementList.listId,
        updateType: 'DELETE',
        listTypes: ['REFINE_BY_LIST']
      }, Object.assign({}, maybeRefinementList, {
        updateType: 'CREATE'
      })];
    }
    return [Object.assign({}, maybeRefinementList, {
      updateType: 'UPDATE'
    })];
  }

  // If we created refinements, send a CREATE update object
  if (!oldRefinementList && maybeRefinementList) {
    return [Object.assign({}, maybeRefinementList, {
      updateType: 'CREATE'
    })];
  }

  // If nothing changed no updates are sent
  return [];
};
export const getFlowEventFilterUpdates = (oldEventFilters, eventFilters) => {
  const updates = [];
  eventFilters.forEach(flowFilter => {
    const isExistingFilter = !!oldEventFilters.find(oldFilter => flowFilter.eventFilterId && flowFilter.eventFilterId.id === oldFilter.eventFilterId.id && flowFilter.flowEventFilterType === oldFilter.flowEventFilterType);

    // The CREATE endpoint doesn't allow us to pass an existing eventFilterId. There can be one
    // here if the user toggled reenrollment, upon which all the event filters need to change their
    // `flowEventFilterType`. The updated filters are sent as CREATE(s) instead of UPDATE(s)
    // since they now represent different filters.
    const bareFlowFilter = _objectWithoutPropertiesLoose(flowFilter, _excluded);
    if (!isExistingFilter) {
      updates.push(Object.assign({}, bareFlowFilter, {
        updateType: 'CREATE'
      }));
    }
  });
  oldEventFilters.forEach(oldFilter => {
    const stillHasFilter = !!eventFilters.find(filter => filter.eventFilterId && filter.eventFilterId.id === oldFilter.eventFilterId.id && filter.flowEventFilterType === oldFilter.flowEventFilterType);
    if (!stillHasFilter) {
      updates.push({
        eventFilterId: oldFilter.eventFilterId,
        flowEventFilterType: oldFilter.flowEventFilterType,
        updateType: 'DELETE'
      });
    }
  });
  return updates;
};
const getOpposingListTriggerForType = listType => {
  return listType === 'ADDED_TO_LIST_TRIGGER' ? 'REMOVED_FROM_LIST_TRIGGER' : 'ADDED_TO_LIST_TRIGGER';
};

// These are only used for List-based "event" triggers, since they can only be added/deleted. Editing is not allowed,
// unlike refinement criteria. In a future where we allow editing of selected events we could consolidate these two
export const getListTriggerAssociatedListUpdates = (oldAssociatedLists, newAssociatedLists) => {
  const updates = [];
  newAssociatedLists.forEach(list => {
    const wasListAdded = !oldAssociatedLists.find(oldList => oldList.listId === list.listId &&
    // There should only be a single list type on the FE when we create one
    oldList.listTypes.includes(list.listTypes[0]));
    if (wasListAdded) {
      updates.push(Object.assign({}, list, {
        updateType: 'CREATE'
      }));
    }
  });
  oldAssociatedLists.forEach(oldList => {
    const wasListDeleted = !newAssociatedLists.find(list => oldList.listId === list.listId &&
    // There should only be a single list type on the FE when we create one
    oldList.listTypes.includes(list.listTypes[0]));
    if (!wasListDeleted) {
      return;
    }

    // Special logic needed to handle the case where we have a single list ID with both list types
    // https://hubspot.slack.com/archives/C01MWLFE12Q/p1668455975016129
    const oppositeOldListType = getOpposingListTriggerForType(oldList.listTypes[0]);
    const hasNewListWithOppositeType = !!newAssociatedLists.find(list => oldList.listId === list.listId && list.listTypes.includes(oppositeOldListType));
    if (hasNewListWithOppositeType) {
      updates.push({
        listId: oldList.listId,
        listTypes: [oppositeOldListType],
        updateType: 'UPDATE'
      });
    } else {
      updates.push({
        listId: oldList.listId,
        listTypes: oldList.listTypes,
        updateType: 'DELETE'
      });
    }
  });
  return updates;
};

// For updating scheduled enrollment list
export const getEnrollmentListAssociatedListUpdates = (oldAssociatedList, newFilterBranch) => {
  const hasFilters = newFilterBranch && newFilterBranch.filterBranches.length > 0;

  // Update case
  if (oldAssociatedList) {
    if (hasFilters) {
      return [Object.assign({}, oldAssociatedList, {
        updateType: 'UPDATE',
        filterBranch: newFilterBranch
      })];
    }

    // Deletion case
    return [Object.assign({}, oldAssociatedList, {
      updateType: 'DELETE'
    })];
  }

  // Creation case
  if (hasFilters) {
    return [{
      updateType: 'CREATE',
      filterBranch: newFilterBranch,
      listTypes: ['ENROLLMENT_LIST']
    }];
  }

  // Shouldn't happen in practice
  return [];
};